import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
// theme.css imports index.css itself, into an explicit `legacy` CSS layer —
// see theme.css's own comment for why that has to be a layered import rather
// than two separate plain <link>/import statements.
import './styles/theme.css';
import { queryClient } from './lib/queryClient';
import App from './App';
import KioskIntake from './components/KioskIntake';
import StatusBoard from './components/StatusBoard';
import MobileUploadPage from './components/MobileUploadPage';
import MobileUploadProcurementPage from './components/MobileUploadProcurementPage';
import QueueStatusPage from './components/QueueStatusPage';
import PlatformConsole from './platform/PlatformConsole';
import { AuthProvider } from './hooks/useAuth';
import EdDisplayBoard from './features/emergency/EdDisplayBoard';

// Entry point, replacing CRA's src/index.js. The query-param dispatch below is
// carried over unchanged — the kiosk, the waiting-room status board, a phone
// that scanned a scan-to-upload QR, and a patient's own queue-status link all
// have no login at all (unattended terminal / public monitor / patient's own
// phone), so none of them mount AuthProvider. Only the staff-facing App does.
//
// This deliberately stays a query-param switch rather than becoming React
// Router routes: these are four separate unauthenticated entry surfaces, not
// pages within the staff app, and their URLs are already embedded in printed
// QR codes and SMS links that must keep resolving.
const params = new URLSearchParams(window.location.search);
const isKiosk = params.has('kiosk');
const isDisplay = params.has('display');
const mobileUploadToken = params.get('mobile_upload');
const mobileUploadProcurementToken = params.get('mobile_upload_procurement');
const queueStatusToken = params.get('queue_status');
// ClintoBytes's own internal tenant-provisioning/management console — see
// platform/PlatformConsole.tsx. Deliberately NOT wrapped in AuthProvider:
// an operator session (lib/platformApi/session.ts) is a structurally
// separate identity domain from tenant staff auth, with its own provider
// (PlatformAuthProvider, mounted inside PlatformConsole itself).
const isPlatform = params.has('platform');

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found in index.html');

const publicSurface = isKiosk ? (
  <KioskIntake />
) : isDisplay && params.get('display') === 'er' ? (
  // The ED tracking board's TV view (?display=er&branch=CODE) — names masked,
  // no clinical detail; see routes/status-board.js GET /emergency.
  <EdDisplayBoard />
) : isDisplay ? (
  <StatusBoard />
) : mobileUploadToken ? (
  <MobileUploadPage token={mobileUploadToken} />
) : mobileUploadProcurementToken ? (
  <MobileUploadProcurementPage token={mobileUploadProcurementToken} />
) : queueStatusToken ? (
  <QueueStatusPage token={queueStatusToken} />
) : isPlatform ? (
  <PlatformConsole />
) : null;

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    {/* QueryClientProvider wraps every surface, not just the staff app — the
        public pages poll the backend too (QueueStatusPage refetches on an
        interval) and benefit from the same retry/staleness policy. */}
    <QueryClientProvider client={queryClient}>
      {publicSurface ?? (
        <AuthProvider>
          <App />
        </AuthProvider>
      )}
    </QueryClientProvider>
  </React.StrictMode>
);
